/* BRIEF 139 §3 — THE PROSPECT AXIS, IN ONE PLACE.
 *
 * §2 built this axis for the two matter lists and spelled all three of its
 * parts — the state type, the row predicate and the pill's options/counts —
 * inside `matterListCfg.tsx`, which is where they belonged when the axis had
 * exactly one consumer. §3 gives the same axis to three money views (Costs &
 * Fees, Reports, Invoicing), so the spelling moves here rather than being
 * copied four times. Rule 3: EXTRACT, don't improve — everything below is
 * `matterListCfg.tsx`'s own spelling as it stood at 7d5ce2b4 (§2's commit),
 * lifted unchanged; `matterListCfg` now calls it and re-exports the type, so
 * every §2 import keeps working untouched. The coordinates are given as that
 * COMMIT rather than as line numbers because this same commit moves the lines
 * they would name.
 *
 * WHAT THE AXIS IS. Orthogonal to every other filter a view already has: a
 * matter passes the prospect axis or it does not, independently of its status,
 * its type, its period or its search. "both" — the ruled default — passes
 * everything, which is why an absent filter and a "both" filter are the same
 * answer, and why a view that has not adopted the axis behaves exactly as it
 * did before.
 *
 * IT APPLIES TO BOTH KINDS OF MATTER. `Case.isProspect` is unit 1's column and
 * `CollectiveCase.isProspect` is its sibling — §2 verified cc rows carry the
 * flag on the wire, and the cc list already filters on it through the shared
 * config. So the predicate is typed on the TAG, not on the model: a money view
 * whose population is "matters" filters both arrays with one value. Filtering
 * only the regular half would let a prospect collective case sit in a total
 * the toggle claims has no prospects in it.
 */

export type ProspectFilterValue = "prospects" | "regular" | "both";

/* The tag, and nothing else — Case and CollectiveCase both satisfy it. */
export type ProspectTaggable = { isProspect?: boolean | null };

/* §2's arm at 7d5ce2b4, verbatim. A null/undefined value is "both": an
   unadopted view and an adopted view showing everything must not diverge. */
export function matterPassesProspect(
  c: ProspectTaggable,
  value: ProspectFilterValue | null | undefined,
): boolean {
  if (!value || value === "both") return true;
  return value === "prospects" ? !!c.isProspect : !c.isProspect;
}

/* The caller's translator, typed to the one option every call here passes.
   Written out rather than taken as `any` (rule 4: no `any` on anything new —
   and the ratchet enforces it: a new file may add none). */
export type ProspectTranslate = (
  key: string,
  opts?: { fallback?: string },
) => string;

/* The pill's three segments, in §2's order (both · regular · prospects) and
   carrying §2's seeded keys — §3 introduces no new user-visible string, so it
   seeds nothing. The fallbacks are §2.6's table verbatim. */
export function prospectFilterOptions(
  t: ProspectTranslate,
): [ProspectFilterValue, string][] {
  return [
    ["both", t("matters.prospect.filter.both", { fallback: "All" })],
    ["regular", t("matters.prospect.filter.regular", { fallback: "Cases" })],
    [
      "prospects",
      t("matters.prospect.filter.prospects", { fallback: "Prospects" }),
    ],
  ];
}

/* Counts over the WHOLE population the view holds, not over what the other
   filters have already selected — §2's toggle counts `rows` whole for the same
   reason (and says so beside `prospectToggle:`), so the pill tells you what
   choosing a segment would show rather than what the current selection
   happens to contain. */
export function prospectFilterCounts(rows: ProspectTaggable[]): {
  both: number;
  regular: number;
  prospects: number;
} {
  return {
    both: rows.length,
    regular: rows.filter((c) => !c.isProspect).length,
    prospects: rows.filter((c) => !!c.isProspect).length,
  };
}
